// C14 — shared form validation.
//
// Each form did its own ad-hoc checking, so the same mistake was caught in one
// place and not another: a future date of birth was accepted everywhere, and a
// negative pup count only produced an error once it reached the server, as a
// 500 with no usable message. These are the checks that are cheap locally and
// meaningful to the user; the server remains authoritative.
//
// Every function returns a string describing the problem, or null when the
// value is acceptable. Callers can therefore do:
//
//   const err = validateDateOfBirth(dob);
//   if (err) return toast.error(err);

const MAX_PUPS_PER_LITTER = 20;

/** End of today, local time — comparing against `new Date()` would reject a
 *  same-day birth recorded later in the day, which is the normal case. */
function endOfToday() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Parse a form value as a LOCAL date.
 *
 * `new Date('2026-08-24')` is specified to parse a date-only string as UTC,
 * while every comparison here is local. West of UTC that gap swallows a whole
 * day: for a user at UTC-4, tomorrow's date parses to 04:00 local today and
 * sails past the future check. Date inputs always produce this format, so the
 * bug would have been the common case, not an edge one.
 */
function parseLocalDate(value) {
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(value);
}

export function validateDateOfBirth(value, { fieldName = 'Date of birth' } = {}) {
  if (!value) return null; // optional; required-ness is the caller's call
  const date = parseLocalDate(value);
  if (Number.isNaN(date.getTime())) return `${fieldName} is not a valid date.`;
  if (date > endOfToday()) return `${fieldName} cannot be in the future.`;

  // A four-digit-year typo (2062 for 2026) is caught above; this catches the
  // other direction, where a mistyped year silently produces an absurd age.
  const veryOld = new Date();
  veryOld.setFullYear(veryOld.getFullYear() - 20);
  if (date < veryOld) return `${fieldName} is more than 20 years ago — check the year.`;

  return null;
}

export function validatePupCount(value, { fieldName = 'Pup count' } = {}) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isInteger(n)) return `${fieldName} must be a whole number.`;
  if (n < 0) return `${fieldName} cannot be negative.`;
  if (n > MAX_PUPS_PER_LITTER) {
    return `${fieldName} of ${n} looks wrong — the maximum is ${MAX_PUPS_PER_LITTER}.`;
  }
  return null;
}

export function validateCapacity(value, { fieldName = 'Capacity' } = {}) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isInteger(n)) return `${fieldName} must be a whole number.`;
  if (n < 1) return `${fieldName} must be at least 1.`;
  return null;
}

/**
 * Genotype allele strings are either diploid `A/B` or the bare wildtype `+`.
 * Anything else is a typo that would otherwise be stored and quietly break
 * genotype matching in search and pedigree.
 */
export function validateGenotypeAllele(value, { fieldName = 'Allele' } = {}) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (trimmed === '+') return null;
  if (/^[^/\s]+\/[^/\s]+$/.test(trimmed)) return null;
  return `${fieldName} "${trimmed}" should look like "+/-" or "+".`;
}

/**
 * Physical tags must be unique within a litter — duplicates make the pups
 * indistinguishable in every downstream view, and the error is far cheaper to
 * catch here than to untangle afterwards.
 *
 * @param {Array<{physical_tag?: string}>} pups
 * @returns {string|null}
 */
export function validateUniqueTags(pups, { fieldName = 'Tag' } = {}) {
  if (!Array.isArray(pups)) return null;

  const seen = new Set();
  for (const pup of pups) {
    const tag = (pup?.physical_tag ?? '').trim();
    if (!tag) continue; // blank tags are allowed; only collisions matter
    const key = tag.toLowerCase();
    if (seen.has(key)) return `${fieldName} "${tag}" is used more than once in this litter.`;
    seen.add(key);
  }
  return null;
}

/**
 * Runs several validators and returns the first problem found, or null.
 * Keeps call sites to one line without hiding which check failed.
 */
export function firstError(...errors) {
  return errors.find(e => e) || null;
}

export const VALIDATION_LIMITS = { MAX_PUPS_PER_LITTER };
